import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  IconButton,
  Button,
  Typography,
  Dialog,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  TextField,
  Stack,
  DialogContent,
  DialogTitle,
  DialogActions,
  Tooltip,
} from "@mui/material";
import Joyride from "react-joyride";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import NoteEditor from "../../new-editor/NoteEditor";
import makeStyles from "@mui/styles/makeStyles";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  ITutorialDto,
  ITutorialJourneyActivatorDto,
  ITutorialJourneyProfileDto,
  TutorialJourneyStatus,
} from "@growth-machine-llc/stridist-api-client";
import TutorialJourneysService from "../../../services/TutorialJourneysService";
import {
  fixLegacyContent,
  unfixLegacyContent,
} from "../../editor/utils/common";
import PromptedLink from "../../../routes/utils/PromtedLink";
import LoadingActionButtonWithStatus from "../../button/LoadingActionButtonWithStatus";
import { TUTORIAL_JOURNEY_ANCHORS } from "../../../constants";
import { ADMIN_JOURNEY_TEMPLATES_ROUTE } from "../../../routes/routes";
import isEqual from "lodash/isEqual";
import JourneyCustomTooltip from "./JourneyCustomTooltip";
import { JourneyProfileInfo } from "./JourneyProfileInfo";
import {
  TUTORIAL_JOURNEYS_QUERY_KEY,
  TUTORIAL_PROFILES_QUERY_KEY,
} from "./TutorialJourneysList";
import { TUTORIAL_JOURNEY_ACTIVATOR_KEY } from "../../../routes/admin/onboardingTemplate/JourneyActivatorTemplate";
import TutorialJourneyActivatorsService from "../../../services/TutorialJourneyActivatorsService";
import { TUTORIAL_JOURNEY_SINGLE_KEY } from "../../../routes/admin/onboardingTemplate/JourneyTemplate";
import { ProfileSelect } from "./ProfileSelect";
import { StepAnchorSelect } from "./StepAnchorSelect";
import { getCookie } from "../../program-component/utils";
import JsonEditor from "../../editor/JsonEditor";
import { Node as SlateNode } from "slate";
import PersonIcon from "@mui/icons-material/Person";
import { colorSystem } from "../../../theme";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
  },
  mainContainer: {
    display: "flex",
    flex: 1,
    marginTop: theme.spacing(2),
  },
  editorContainer: {
    flex: 1,
    maxWidth: "50%",
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: theme.spacing(2),
  },
  previewContainer: {
    width: 300,
    flex: 1,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: theme.spacing(2),
  },
  stepToolbox: {
    position: "absolute",
    top: theme.spacing(2),
    left: "55%",
    transform: "translateX(-50%)",
    zIndex: theme.zIndex.tooltip,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[4],
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: 1,
    cursor: "pointer",
    border: `1px solid ${colorSystem.gray5}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: 320,
  },
  toolboxInfo: {
    flex: 1,
    minWidth: 0,
  },
  toolboxButton: {
    marginLeft: theme.spacing(2),
    textTransform: "none",
    fontWeight: 500,
    borderRadius: theme.shape.borderRadius,
  },
  navButtons: {
    position: "absolute",
    top: "50%",
    right: theme.spacing(2),
    transform: "translateY(-50%)",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    zIndex: theme.zIndex.tooltip,
  },
  reorderDialogContainer: {
    minHeight: 200,
    borderRadius: 8,
    padding: theme.spacing(1),
    transition: "all 0.2s ease",
    "&.dragOver": {
      backgroundColor: colorSystem.gray10,
      border: `2px dashed ${colorSystem.blueDark}`,
    },
  },
  reorderCard: {
    backgroundColor: colorSystem.white,
    border: `1px solid ${colorSystem.gray5}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    cursor: "grab",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    "&.dragging": {
      backgroundColor: colorSystem.blueLightest,
      border: `2px solid ${colorSystem.blueDark}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      cursor: "grabbing",
    },
  },

  reorderAvatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
  },
  reorderAvatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: colorSystem.gray4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  reorderContent: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },

  reorderStepTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: 2,
  },

  reorderAnchor: {
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginBottom: 2,
  },

  reorderPreview: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    opacity: 0.8,
  },

  reorderIcon: {
    color: colorSystem.gray4,
    fontSize: 20,
    flexShrink: 0,
  },
  reorderOpenButton: {
    textTransform: "none",
    fontSize: 14,
    padding: theme.spacing(0.5, 1.25),
    minWidth: 60,
    fontWeight: 500,
    borderColor: colorSystem.gray4,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: colorSystem.white3,
      borderColor: colorSystem.gray3,
    },
  },
}));

function createEmptyStepContent() {
  return [{ type: "paragraph", children: [{ text: "" }] }];
}

interface IUpdateJourneyPayload {
  id: number;
  content: string;
  status?: TutorialJourneyStatus;
  profileId?: number | null;
}

export interface JourneyTemplateEditorProps {
  data: ITutorialDto | ITutorialJourneyActivatorDto;
  profiles: ITutorialJourneyProfileDto[];
  displayMode?: "joyride" | "dialog";
}

const isTutorialActivator = (
  tutorialJourney: ITutorialDto | ITutorialJourneyActivatorDto,
): tutorialJourney is ITutorialJourneyActivatorDto =>
  "content" in tutorialJourney;

function extractPreview(content: any): string {
  if (!content) return "";
  if (typeof content === "string") {
    const firstLine = content.split("\n").find((l) => l.trim().length > 0);
    return firstLine ? firstLine.replace(/[#*_>`]/g, "").slice(0, 80) : "";
  }
  if (Array.isArray(content)) {
    const text = content
      .flatMap((node) => node.children?.map((c: any) => c.text) || [])
      .join(" ")
      .trim();
    return text.slice(0, 80);
  }
  return "";
}

export function JourneyTemplateEditor(props: JourneyTemplateEditorProps) {
  const { data, profiles, displayMode = "joyride" } = props;
  const s = useStyles();
  const queryClient = useQueryClient();

  const previewRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const isActivator = isTutorialActivator(data);
  const initialPublished =
    !isActivator &&
    (data as ITutorialDto).status === TutorialJourneyStatus.PUBLISHED;
  const [isPublished, setIsPublished] = useState(initialPublished);
  const [originalIsPublished] = useState(initialPublished);
  const devCookie = getCookie("stabilization");
  const isDevMode = devCookie === "true";

  const parsedInitialContent = useMemo(() => {
    if (isActivator) {
      if (data.content) {
        try {
          const parsed = JSON.parse(data.content);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return createEmptyStepContent();
    } else {
      if (data.steps && data.steps.length > 0) {
        return data.steps.map((step) => {
          if (!step.content) return createEmptyStepContent();
          if (typeof step.content === "string") {
            return fixLegacyContent(JSON.parse(step.content));
          }
          return step.content;
        });
      }
      return [createEmptyStepContent()];
    }
  }, [data]);

  const parsedInitialProfiles = useMemo(() => {
    if (isActivator) return [];
    if (!data.steps) return [];
    return data.steps.map((step) => {
      if (!step.profile) return null;
      const matched = profiles.find((p) => p.name === step.profile!.name);
      return {
        showTooltip: true,
        name: step.profile!.name,
        subtitle: step.profile!.subtitle,
        avatar: step.profile!.avatar,
        id: matched ? matched.id : null,
      };
    });
  }, [data, profiles]);

  const initialActivatorProfile = useMemo(() => {
    if (!isActivator) return null;
    if (!data.profile) return null;
    const matched = profiles.find((p) => p.name === data.profile!.name);
    return matched || { ...data.profile, id: null };
  }, [data, profiles]);

  const tutorialType = !isActivator ? (data as ITutorialDto).type : undefined;

  const initializeStepAnchors = (): string[] => {
    if (!tutorialType) return Array(parsedInitialContent.length).fill("");
    if (!isActivator && data.steps && data.steps.length > 0) {
      return data.steps.map((step) => step.anchor || "");
    }
    const anchorsForType = TUTORIAL_JOURNEY_ANCHORS[tutorialType];
    const options = Object.keys(anchorsForType);
    return parsedInitialContent.map((_, idx) => options[idx] || options[0]);
  };

  const [stepContents, setStepContents] = useState<any[]>(parsedInitialContent);
  const [stepProfiles, setStepProfiles] = useState<any[]>(
    isActivator ? [] : parsedInitialProfiles,
  );
  const [activatorProfile, setActivatorProfile] =
    useState<ITutorialJourneyProfileDto | null>(initialActivatorProfile);
  const [stepAnchors, setStepAnchors] = useState<string[]>(
    initializeStepAnchors(),
  );
  const [originalContents, setOriginalContents] =
    useState<any[]>(parsedInitialContent);
  const [originalProfiles, setOriginalProfiles] = useState<any[]>(
    parsedInitialProfiles,
  );
  const [originalActivatorProfile, setOriginalActivatorProfile] =
    useState<ITutorialJourneyProfileDto | null>(initialActivatorProfile);
  const [originalAnchors, setOriginalAnchors] = useState<string[]>(
    initializeStepAnchors(),
  );
  const [currentStep, setCurrentStep] = useState<number>(0);

  useEffect(() => {
    if (!data?.id) return;
    setOriginalContents(parsedInitialContent);
    setStepContents(parsedInitialContent);
    if (isActivator) {
      setOriginalActivatorProfile(initialActivatorProfile);
      setActivatorProfile(initialActivatorProfile);
    } else {
      setOriginalProfiles(parsedInitialProfiles);
      setStepProfiles(parsedInitialProfiles);
      const initialAnchors = initializeStepAnchors();
      setStepAnchors(initialAnchors);
      setOriginalAnchors(initialAnchors);
    }
  }, [
    data,
    parsedInitialContent,
    parsedInitialProfiles,
    initialActivatorProfile,
    isActivator,
  ]);

  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    let dirty = !isEqual(stepContents, originalContents);
    if (isActivator) {
      dirty = dirty || !isEqual(activatorProfile, originalActivatorProfile);
    } else {
      dirty = dirty || !isEqual(stepProfiles, originalProfiles);
      dirty = dirty || !isEqual(stepAnchors, originalAnchors);
    }
    dirty = dirty || isPublished !== originalIsPublished;
    setIsDirty(dirty);
  }, [
    stepContents,
    originalContents,
    activatorProfile,
    originalActivatorProfile,
    stepProfiles,
    originalProfiles,
    stepAnchors,
    originalAnchors,
    isActivator,
    isPublished,
    originalIsPublished,
  ]);

  const [steps, setSteps] = useState<any[]>([]);
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState<boolean>(false);
  const [stepIds, setStepIds] = useState<string[]>([]);

  useEffect(() => {
    if (isActivator) {
      setSteps([]);
      return;
    }
    const newSteps = stepContents.map((content, index) => {
      const p = stepProfiles[index];
      const profileForStep =
        p && p.showTooltip
          ? { name: p.name, subtitle: p.subtitle, avatar: p.avatar }
          : null;
      return {
        target: targetRef.current || previewRef.current || document.body,
        content: (
          <NoteEditor
            value={content}
            readOnly
            onChange={() => {}}
            style={{
              border: "none",
              background: "transparent",
              minHeight: 60,
              minWidth: 200,
            }}
          />
        ),
        meta: { profile: profileForStep },
        placement: "right",
        disableBeacon: true,
        disableOverlay: true,
        floaterProps: { disableFlip: true },
      };
    });
    setSteps(newSteps);
  }, [stepContents, stepProfiles, isActivator]);

  const handleOpenOrderDialog = () => {
    if (isActivator) return;
    // Initialize stable ids for draggables on open
    setStepIds((prev) => {
      if (prev.length === stepContents.length) return prev;
      const base = String(data?.id ?? "journey");
      return Array.from(
        { length: stepContents.length },
        (_, i) => `${base}-step-${i}`,
      );
    });
    setIsOrderDialogOpen(true);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    // Create new arrays with reordered items
    const newStepContents = Array.from(stepContents);
    const newStepAnchors = Array.from(stepAnchors);
    const newStepProfiles = Array.from(stepProfiles);

    // Remove item from source and insert at destination
    const [movedContent] = newStepContents.splice(sourceIndex, 1);
    const [movedAnchor] = newStepAnchors.splice(sourceIndex, 1);
    const [movedProfile] = newStepProfiles.splice(sourceIndex, 1);

    newStepContents.splice(destinationIndex, 0, movedContent);
    newStepAnchors.splice(destinationIndex, 0, movedAnchor);
    newStepProfiles.splice(destinationIndex, 0, movedProfile);

    setStepContents(newStepContents);
    setStepAnchors(newStepAnchors);
    setStepProfiles(newStepProfiles);

    // Reorder stable ids to match the moved item
    setStepIds((prev) => {
      const next = Array.from(prev);
      const [movedId] = next.splice(sourceIndex, 1);
      next.splice(destinationIndex, 0, movedId);
      return next;
    });
  };

  const { mutate: updateJourneyMutation, status } = useMutation({
    mutationFn: (payload: IUpdateJourneyPayload) =>
      displayMode === "dialog"
        ? TutorialJourneyActivatorsService.updateJourneyActivator(payload)
        : TutorialJourneysService.updateTutorialJourney(payload),
    onSuccess: () => {
      setOriginalContents([...stepContents]);
      if (isActivator) {
        setOriginalActivatorProfile(activatorProfile);
      } else {
        setOriginalProfiles([...stepProfiles]);
        setOriginalAnchors([...stepAnchors]);
      }
      queryClient.invalidateQueries({
        queryKey: [TUTORIAL_JOURNEYS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [TUTORIAL_PROFILES_QUERY_KEY],
      });
      const singleJourneyQueryKey = tutorialType
        ? [TUTORIAL_JOURNEY_SINGLE_KEY, tutorialType]
        : [TUTORIAL_JOURNEY_SINGLE_KEY];
      queryClient.refetchQueries({ queryKey: singleJourneyQueryKey });
      queryClient.refetchQueries({
        queryKey: [TUTORIAL_JOURNEY_ACTIVATOR_KEY, data?.id],
      });
    },
  });

  const handleManualSave = () => {
    const payloadContent = isActivator
      ? JSON.stringify(unfixLegacyContent(stepContents))
      : JSON.stringify({
          steps: stepContents.map((content, index) => ({
            stepIndex: index,
            content: unfixLegacyContent(content),
            journeyStepProfileId: stepProfiles[index]?.id ?? null,
            anchor: stepAnchors[index],
          })),
        });
    const payload: IUpdateJourneyPayload = {
      id: data.id!,
      content: payloadContent,
    };
    if (!isActivator) {
      payload.status = isPublished
        ? TutorialJourneyStatus.PUBLISHED
        : TutorialJourneyStatus.DRAFT;
    }
    if (isActivator) {
      payload.profileId = activatorProfile?.id ?? null;
    }
    updateJourneyMutation(payload);
  };

  const handleAddStep = () => {
    if (isActivator) return;

    setStepContents((prev) => {
      const next = [...prev];
      next.splice(currentStep + 1, 0, createEmptyStepContent());
      return next;
    });
    setStepProfiles((prev) => {
      const next = [...prev];
      next.splice(currentStep + 1, 0, null);
      return next;
    });
    setStepAnchors((prev) => {
      const next = [...prev];
      const options = Object.keys(TUTORIAL_JOURNEY_ANCHORS[tutorialType] || {});
      const used = new Set(next);
      const free = options.find((opt) => !used.has(opt)) || options[0];
      next.splice(currentStep + 1, 0, free);
      return next;
    });

    setCurrentStep((prev) => prev + 1);
  };

  const handleRemoveStep = () => {
    if (isActivator || stepContents.length <= 1) return;
    let nextStep = currentStep;
    if (currentStep === stepContents.length - 1) nextStep = currentStep - 1;
    setStepContents((prev) => {
      const updated = [...prev];
      updated.splice(currentStep, 1);
      return updated;
    });
    setStepProfiles((prev) => {
      const updated = [...prev];
      updated.splice(currentStep, 1);
      return updated;
    });
    setStepAnchors((prev) => {
      const updated = [...prev];
      updated.splice(currentStep, 1);
      return updated;
    });
    setCurrentStep(nextStep);
  };

  const handlePrevStep = () => {
    if (!isActivator && currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  const handleNextStep = () => {
    if (!isActivator) {
      if (currentStep < stepContents.length - 1) {
        setCurrentStep((prev) => prev + 1);
      } else {
        handleAddStep();
      }
    }
  };

  const handleStepProfileSelect = (profileId: number) => {
    const selected = profiles.find((p) => p.id === profileId) || null;
    setStepProfiles((prev) => {
      const updated = [...prev];
      updated[currentStep] = selected
        ? {
            showTooltip: true,
            name: selected.name,
            avatar: selected.avatar,
            subtitle: selected.subtitle,
            id: selected.id,
          }
        : null;
      return updated;
    });
  };

  const handleActivatorProfileSelect = (profileId: number) => {
    const selected = profiles.find((p) => p.id === profileId) || null;
    setActivatorProfile(selected);
  };

  const handleStepAnchorSelect = (anchor: string) => {
    setStepAnchors((prev) => {
      const updated = [...prev];
      updated[currentStep] = anchor;
      return updated;
    });
  };

  const selectedProfileId: number | null = isActivator
    ? activatorProfile?.id ?? null
    : stepProfiles[currentStep]?.id ?? null;

  const currentContent = isActivator
    ? stepContents
    : stepContents[currentStep] ?? createEmptyStepContent();

  const handleEditorChange = (newValue: any) => {
    if (isActivator) {
      setStepContents(newValue);
    } else {
      setStepContents((prev) => {
        const updated = [...prev];
        updated[currentStep] = newValue;
        return updated;
      });
    }
  };

  const displayProfile = isActivator
    ? activatorProfile
    : stepProfiles[currentStep];
  const title = isActivator
    ? `Activator for ${(data as ITutorialJourneyActivatorDto).tutorialJourneyType}`
    : (data as ITutorialDto).title;

  const jsonEditorValue: SlateNode[] = useMemo(() => {
    return isActivator
      ? (stepContents as unknown as SlateNode[])
      : ((stepContents[currentStep] ??
          createEmptyStepContent()) as SlateNode[]);
  }, [isActivator, stepContents, currentStep]);

  const handleStepSelect = (index: number) => {
    setCurrentStep(index);
    setIsOrderDialogOpen(false);
  };
  const getAvailableAnchors = (): string[] => {
    if (!tutorialType) return [];
    const options = Object.keys(TUTORIAL_JOURNEY_ANCHORS[tutorialType] || {});
    const used = new Set(stepAnchors);
    return options.filter((opt) => !used.has(opt));
  };

  const isAddStepDisabled = getAvailableAnchors().length === 0;

  return (
    <>
      <Box className={s.root}>
        <Box sx={{ mb: 1 }}>
          <PromptedLink
            to={
              isActivator
                ? `${ADMIN_JOURNEY_TEMPLATES_ROUTE}?tab=activators`
                : `${ADMIN_JOURNEY_TEMPLATES_ROUTE}?tab=journeys`
            }
            isDirty={isDirty}
          >
            <Button variant="text" startIcon={<ArrowBackIcon />}>
              Back to templates
            </Button>
          </PromptedLink>
        </Box>
        <Box>
          <Typography sx={{ mb: 2 }} variant="h3">
            {title}
          </Typography>
        </Box>
        <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
          <LoadingActionButtonWithStatus
            onClick={handleManualSave}
            status={status}
            disabled={!isDirty}
            size="large"
            delay={2000}
            sx={{ width: 180 }}
          >
            <Box sx={{ px: 1 }}>Save changes</Box>
          </LoadingActionButtonWithStatus>
          {!isActivator && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                />
              }
              label="Published for users"
            />
          )}
        </Box>
        <Box className={s.mainContainer}>
          <Box className={s.editorContainer}>
            {!isActivator && (
              <StepAnchorSelect
                tutorialType={tutorialType}
                value={stepAnchors[currentStep] || ""}
                selectedAnchors={stepAnchors}
                onChange={handleStepAnchorSelect}
              />
            )}
            <ProfileSelect
              value={selectedProfileId}
              profiles={profiles}
              onChange={(val) =>
                isActivator
                  ? handleActivatorProfileSelect(val)
                  : handleStepProfileSelect(val!)
              }
            />
            <NoteEditor
              key={`step-editor-${isActivator ? "activator" : currentStep}`}
              value={currentContent}
              readOnly={false}
              onChange={handleEditorChange}
            />
          </Box>
          <Tooltip
            title="This is a preview — buttons are disabled"
            placement="left"
            arrow
          >
            <Box className={s.previewContainer} ref={previewRef}>
              {displayMode === "joyride" && !isActivator && (
                <>
                  <Box className={s.stepToolbox}>
                    <Box className={s.toolboxInfo}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Step {currentStep + 1} of {steps.length}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={((currentStep + 1) / steps.length) * 100}
                        sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      className={s.toolboxButton}
                      onClick={handleOpenOrderDialog}
                    >
                      Reorder
                    </Button>
                  </Box>

                  <Box
                    ref={targetRef}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "-650px",
                      transform: "translateY(-50%)",
                      width: 1,
                      height: 1,
                      pointerEvents: "none",
                    }}
                  />

                  <Joyride
                    key={`joyride_${steps.length}_${currentStep}`}
                    steps={steps}
                    floaterProps={{ hideArrow: true }}
                    run
                    stepIndex={currentStep}
                    continuous={false}
                    showSkipButton={false}
                    showProgress={false}
                    tooltipComponent={(p: any) => {
                      const { profile } = p.step?.meta || {};
                      return (
                        <JourneyCustomTooltip
                          {...p}
                          profileName={profile?.name || ""}
                          profileAvatar={profile?.avatar || ""}
                          profileSubtitle={profile?.subtitle}
                          showProfileInfo={!!profile}
                        />
                      );
                    }}
                    styles={{ options: { zIndex: 10000 } }}
                  />

                  <Box className={s.navButtons}>
                    <IconButton
                      onClick={handlePrevStep}
                      disabled={currentStep === 0}
                    >
                      <ArrowBackIcon />
                    </IconButton>

                    <IconButton
                      onClick={handleNextStep}
                      disabled={currentStep === steps.length - 1}
                    >
                      <ArrowForwardIcon />
                    </IconButton>

                    <IconButton
                      onClick={handleAddStep}
                      disabled={isAddStepDisabled}
                    >
                      <AddIcon />
                    </IconButton>

                    {steps.length > 1 && (
                      <IconButton onClick={handleRemoveStep}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </>
              )}

              {displayMode === "dialog" && (
                <Dialog
                  open
                  hideBackdrop
                  disablePortal
                  disableEnforceFocus
                  disableAutoFocus
                  disableScrollLock
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 0,
                    transform: "translateY(-50%)",
                    m: 0,
                    zIndex: 1000,
                    maxWidth: "100%",
                    maxHeight: "100vh",
                  }}
                  PaperProps={{
                    sx: {
                      pointerEvents: "auto",
                      boxShadow: 3,
                      padding: 2,
                      border: "1px solid #ccc",
                      borderRadius: 1,
                      backgroundColor: "#fff",
                      overflow: "visible",
                      width: "auto",
                      height: "auto",
                      maxWidth: "100%",
                      maxHeight: "100vh",
                    },
                  }}
                >
                  <Box
                    key={`dialog-${currentStep}-${JSON.stringify(currentContent)}`}
                  >
                    {displayProfile &&
                      (displayProfile.name || displayProfile.avatar) && (
                        <JourneyProfileInfo
                          profileName={displayProfile.name || ""}
                          profileAvatar={displayProfile.avatar || ""}
                          profileSubtitle={displayProfile.subtitle || ""}
                        />
                      )}
                    <Box mt={displayProfile?.name ? 2 : 0}>
                      <NoteEditor
                        value={currentContent}
                        readOnly
                        onChange={() => {}}
                      />
                    </Box>
                  </Box>
                </Dialog>
              )}
            </Box>
          </Tooltip>
        </Box>
      </Box>
      {!isActivator && (
        <Dialog
          open={isOrderDialogOpen}
          onClose={() => setIsOrderDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          sx={{ zIndex: 200001 }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" component="div">
              Edit Step Order
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drag and drop steps to reorder or click “Open” to view a specific
              one
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: 3, py: 2 }}>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="steps">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={clsx(
                      s.reorderDialogContainer,
                      snapshot.isDraggingOver && "dragOver",
                    )}
                  >
                    {stepContents.map((_, idx) => {
                      const profile = stepProfiles[idx];
                      const hasProfile = !!profile?.avatar;
                      const hasContent = !!stepContents[idx];

                      return (
                        <Draggable
                          key={stepIds[idx] || idx}
                          draggableId={stepIds[idx] || `step-${idx}`}
                          index={idx}
                        >
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={clsx(
                                s.reorderCard,
                                snapshot.isDragging && "dragging",
                                idx === currentStep && "activeStep",
                              )}
                            >
                              <Box
                                {...provided.dragHandleProps}
                                className={s.reorderIcon}
                              >
                                ⋮⋮
                              </Box>

                              {hasProfile ? (
                                <Box
                                  component="img"
                                  src={profile.avatar}
                                  alt={profile.name || "Profile avatar"}
                                  className={s.reorderAvatar}
                                />
                              ) : (
                                <Box
                                  className={s.reorderAvatarPlaceholder}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <PersonIcon
                                    sx={{ color: "#fff", fontSize: 20 }}
                                  />
                                </Box>
                              )}
                              <Box className={s.reorderContent}>
                                <Typography
                                  variant="subtitle2"
                                  className={s.reorderStepTitle}
                                >
                                  Step {idx + 1}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  className={s.reorderAnchor}
                                >
                                  {stepAnchors[idx] || "(no anchor)"}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  className={s.reorderPreview}
                                  color={
                                    !hasContent
                                      ? "text.disabled"
                                      : "text.primary"
                                  }
                                >
                                  {hasContent
                                    ? extractPreview(stepContents[idx])
                                    : "(no content)"}
                                </Typography>
                              </Box>

                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleStepSelect(idx)}
                                className={s.reorderOpenButton}
                              >
                                Open
                              </Button>
                            </Box>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                    {stepContents.length === 0 && (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 4,
                          color: "text.secondary",
                        }}
                      >
                        <Typography variant="body2">
                          No steps to reorder
                        </Typography>
                      </Box>
                    )}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setIsOrderDialogOpen(false)}
              variant="outlined"
              sx={{ minWidth: 100 }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {isDevMode && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" gutterBottom>
            Raw JSON preview
          </Typography>
          <JsonEditor
            content={jsonEditorValue}
            onContentChange={(newVal) => {
              if (isActivator) {
                setStepContents(newVal as any);
              } else {
                handleEditorChange(newVal as any);
              }
            }}
          />
        </Box>
      )}
    </>
  );
}

export default JourneyTemplateEditor;
